import { useEffect, useRef, useState } from 'react'
import { Camera, ScanLine, Trash2 } from 'lucide-react'
import { useDatos } from '../store/datos'
import { useSesion, usuarioActual } from '../store/sesion'
import { useUI } from '../store/ui'
import { Hoja } from './ui/Hoja'
import { Boton, Campo, Entrada, Lista } from './ui/base'
import { EscanerCodigo } from './Escaner'
import { comprimirImagen } from '../lib/imagen'
import { fotoApp } from '../lib/fotos'
import { uid } from '../lib/cn'
import { money } from '../lib/format'
import { sugerirSku } from '../lib/sku'
import { CATEGORIAS, type Producto, type Unidad } from '../types'

const UNIDADES: { valor: Unidad; etiqueta: string }[] = [
  { valor: 'unidad', etiqueta: 'Unidad' },
  { valor: 'g', etiqueta: 'Gramos' },
  { valor: 'kg', etiqueta: 'Kilos' },
  { valor: 'litro', etiqueta: 'Litros' },
  { valor: 'bandeja', etiqueta: 'Bandeja' },
  { valor: 'caja', etiqueta: 'Caja' },
]

const vacio = (): Producto => ({
  id: uid('p'),
  codigo: '',
  nombre: '',
  categoria: 'frutas',
  peso: 0,
  unidad: 'unidad',
  precioCosto: 0,
  precioSugerido: 0,
  stock: 0,
  reservado: 0,
  stockMinimo: null,
  activo: true,
  creado: new Date().toISOString(),
})

export function FormularioProducto({
  abierto,
  onCerrar,
  producto,
}: {
  abierto: boolean
  onCerrar: () => void
  producto?: Producto
}) {
  const guardarProducto = useDatos((s) => s.guardarProducto)
  const productos = useDatos((s) => s.productos)
  const iva = useDatos((s) => s.ajustes.porcentajeIva) || 19
  const avisar = useUI((s) => s.avisar)
  const esAdmin = useSesion((s) => s.usuario?.rol === 'admin')
  const [forma, setForma] = useState<Producto>(producto ?? vacio())
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [guardando, setGuardando] = useState(false)
  const [escaner, setEscaner] = useState(false)
  // Mientras nadie escriba el SKU a mano, en un producto nuevo se va
  // proponiendo solo a partir del nombre y el peso. Apenas se toca el
  // campo, manda lo que se escribió.
  const [skuTocado, setSkuTocado] = useState(false)
  const fotoRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (abierto) {
      setForma(producto ? { ...producto } : vacio())
      setErrores({})
      setSkuTocado(false)
    }
  }, [abierto, producto])

  useEffect(() => {
    if (producto || skuTocado) return
    setForma((f) => ({ ...f, codigo: sugerirSku(f, productos) }))
  }, [producto, skuTocado, forma.nombre, forma.peso, forma.unidad, productos])

  const set = <K extends keyof Producto>(campo: K, valor: Producto[K]) =>
    setForma((f) => ({ ...f, [campo]: valor }))

  const validar = () => {
    const e: Record<string, string> = {}
    if (!forma.nombre.trim()) e.nombre = 'Escribe el nombre del producto.'
    if (!forma.codigo.trim()) e.codigo = 'El código identifica al producto en la bodega.'
    else if (
      productos.some(
        (p) => p.id !== forma.id && p.codigo.toLowerCase() === forma.codigo.trim().toLowerCase(),
      )
    )
      e.codigo = 'Ya existe un producto con ese código.'
    if (forma.precioSugerido <= 0) e.precioSugerido = 'Indica a qué precio se vende.'
    if (forma.precioCosto < 0) e.precioCosto = 'El costo no puede ser negativo.'
    setErrores(e)
    return Object.keys(e).length === 0
  }

  const enviar = async (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!validar()) return
    setGuardando(true)
    try {
      await guardarProducto(
        { ...forma, codigo: forma.codigo.trim().toUpperCase(), nombre: forma.nombre.trim() },
        usuarioActual(),
      )
      avisar(producto ? 'Producto actualizado' : 'Producto creado')
      onCerrar()
    } catch (err) {
      avisar(err instanceof Error ? err.message : 'No se pudo guardar.', 'error')
    } finally {
      setGuardando(false)
    }
  }

  const subirFoto = async (archivo?: File) => {
    if (!archivo) return
    try {
      set('foto', await comprimirImagen(archivo))
    } catch {
      avisar('No se pudo procesar la imagen.', 'error')
    }
  }

  const margen =
    forma.precioSugerido > 0
      ? ((forma.precioSugerido - forma.precioCosto) / forma.precioSugerido) * 100
      : 0

  return (
    <>
      <Hoja
        abierta={abierto}
        onCerrar={onCerrar}
        titulo={producto ? 'Editar producto' : 'Nuevo producto'}
        descripcion={producto ? producto.codigo : 'Los datos base del producto en bodega'}
        pie={
          <>
            <Boton variante="secundario" bloque onClick={onCerrar}>
              Cancelar
            </Boton>
            <Boton bloque cargando={guardando} onClick={enviar}>
              {producto ? 'Guardar cambios' : 'Crear producto'}
            </Boton>
          </>
        }
      >
        <form onSubmit={enviar} className="space-y-4 pb-2">
          {/* Foto */}
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => fotoRef.current?.click()}
              className="grid size-20 shrink-0 place-items-center overflow-hidden rounded-2xl border-2 border-dashed borde-base texto-tenue transition-colors hover:border-bosque-400 hover:text-bosque-500"
            >
              {forma.foto ? (
                <img
                  src={fotoApp(forma.foto)}
                  alt=""
                  className="size-full object-cover"
                  decoding="async"
                />
              ) : (
                <Camera className="size-6" />
              )}
            </button>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold texto-base">Foto del producto</p>
              <p className="mt-0.5 text-[12px] texto-suave">
                Ayuda a reconocerlo rápido en el listado. Opcional.
              </p>
              {forma.foto && (
                <button
                  type="button"
                  onClick={() => set('foto', undefined)}
                  className="mt-1.5 inline-flex items-center gap-1 text-[12px] font-semibold text-rojo-500 hover:underline"
                >
                  <Trash2 className="size-3.5" /> Quitar foto
                </button>
              )}
            </div>
            <input
              ref={fotoRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => void subirFoto(e.target.files?.[0])}
            />
          </div>

          <Campo etiqueta="Nombre" requerido error={errores.nombre}>
            <Entrada
              value={forma.nombre}
              onChange={(e) => set('nombre', e.target.value)}
              placeholder="Arándanos frescos"
              autoFocus
            />
          </Campo>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              etiqueta="SKU"
              requerido
              error={errores.codigo}
              ayuda={producto ? undefined : 'Se propone solo con el nombre y el peso. Puedes cambiarlo.'}
            >
              <Entrada
                value={forma.codigo}
                onChange={(e) => {
                  setSkuTocado(true)
                  set('codigo', e.target.value.toUpperCase())
                }}
                placeholder="NUE-KG"
                className="codigo"
              />
            </Campo>
            <Campo etiqueta="Categoría">
              <Lista
                value={forma.categoria}
                onChange={(e) => set('categoria', e.target.value as Producto['categoria'])}
              >
                {CATEGORIAS.map((c) => (
                  <option key={c.valor} value={c.valor}>
                    {c.etiqueta}
                  </option>
                ))}
              </Lista>
            </Campo>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo etiqueta="Contenido por unidad" ayuda="Deja 0 si no aplica.">
              <Entrada
                type="number"
                min={0}
                step="any"
                inputMode="decimal"
                value={forma.peso || ''}
                onChange={(e) => set('peso', Number(e.target.value))}
                placeholder="125"
              />
            </Campo>
            <Campo etiqueta="Unidad">
              <Lista value={forma.unidad} onChange={(e) => set('unidad', e.target.value as Unidad)}>
                {UNIDADES.map((u) => (
                  <option key={u.valor} value={u.valor}>
                    {u.etiqueta}
                  </option>
                ))}
              </Lista>
            </Campo>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {esAdmin && (
              <Campo
                etiqueta="Precio de costo"
                error={errores.precioCosto}
                ayuda="Solo el administrador lo ve. Nunca aparece para vendedores."
              >
                <Entrada
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={forma.precioCosto || ''}
                  onChange={(e) => set('precioCosto', Number(e.target.value))}
                  placeholder="1200"
                />
              </Campo>
            )}
            <Campo
              etiqueta="Precio sugerido"
              requerido
              error={errores.precioSugerido}
              ayuda={
                esAdmin && forma.precioSugerido > 0 && forma.precioCosto > 0
                  ? `Margen de ${Math.round(margen)}%`
                  : undefined
              }
            >
              <Entrada
                type="number"
                min={0}
                inputMode="numeric"
                value={forma.precioSugerido || ''}
                onChange={(e) => set('precioSugerido', Number(e.target.value))}
                placeholder="2490"
              />
            </Campo>
          </div>

          {esAdmin && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                etiqueta="Precio mínimo — mayorista"
                ayuda={
                  forma.precioMinimoMayorista
                    ? `Piso a pastelerías y clientes grandes · con IVA ${money(Math.round(forma.precioMinimoMayorista * (1 + iva / 100)))}`
                    : 'Piso de venta a pastelerías y clientes grandes. Esto sí lo ve el vendedor.'
                }
              >
                <Entrada
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={forma.precioMinimoMayorista ?? ''}
                  onChange={(e) =>
                    set(
                      'precioMinimoMayorista',
                      e.target.value === '' ? undefined : Number(e.target.value),
                    )
                  }
                  placeholder="Sin definir"
                />
              </Campo>
              <Campo
                etiqueta="Precio mínimo — menos de 3 kilos"
                ayuda={
                  forma.precioMinimoDetalle
                    ? `Piso cuando la cantidad es chica · con IVA ${money(Math.round(forma.precioMinimoDetalle * (1 + iva / 100)))}`
                    : 'Piso de venta cuando la cantidad es chica.'
                }
              >
                <Entrada
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={forma.precioMinimoDetalle ?? ''}
                  onChange={(e) =>
                    set(
                      'precioMinimoDetalle',
                      e.target.value === '' ? undefined : Number(e.target.value),
                    )
                  }
                  placeholder="Sin definir"
                />
              </Campo>
              <Campo
                etiqueta="Precio mínimo — mayorista +10 kg"
                ayuda={
                  forma.precioMinimoMayoristaVolumen
                    ? `Piso cuando el pedido llega a 10 kilos · con IVA ${money(Math.round(forma.precioMinimoMayoristaVolumen * (1 + iva / 100)))}`
                    : 'Piso más barato cuando el pedido de este producto llega a 10 kilos o más.'
                }
              >
                <Entrada
                  type="number"
                  min={0}
                  inputMode="numeric"
                  value={forma.precioMinimoMayoristaVolumen ?? ''}
                  onChange={(e) =>
                    set(
                      'precioMinimoMayoristaVolumen',
                      e.target.value === '' ? undefined : Number(e.target.value),
                    )
                  }
                  placeholder="Sin definir"
                />
              </Campo>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              etiqueta={producto ? 'Stock' : 'Stock inicial'}
              ayuda={producto ? 'Si lo cambias, queda registrado como ajuste.' : undefined}
            >
              <Entrada
                type="number"
                min={0}
                inputMode="numeric"
                value={forma.stock || ''}
                onChange={(e) => set('stock', Number(e.target.value))}
                placeholder="0"
              />
            </Campo>
            <Campo etiqueta="Aviso de poco stock" ayuda="Vacío usa el umbral general.">
              <Entrada
                type="number"
                min={0}
                inputMode="numeric"
                value={forma.stockMinimo ?? ''}
                onChange={(e) =>
                  set('stockMinimo', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder="10"
              />
            </Campo>
          </div>

          {esAdmin && (
            <Campo
              etiqueta="Orden en el catálogo"
              ayuda="Más chico aparece más arriba en Inventario. Vacío lo manda al final, por nombre."
            >
              <Entrada
                type="number"
                inputMode="numeric"
                value={forma.ordenCatalogo ?? ''}
                onChange={(e) =>
                  set('ordenCatalogo', e.target.value === '' ? undefined : Number(e.target.value))
                }
                placeholder="Ej: 10"
              />
            </Campo>
          )}

          <Campo etiqueta="Código de barras" ayuda="Para escanearlo al vender o ingresar stock.">
            <div className="flex gap-2">
              <Entrada
                value={forma.codigoBarras ?? ''}
                onChange={(e) => set('codigoBarras', e.target.value)}
                placeholder="7801234567890"
                inputMode="numeric"
                className="codigo"
              />
              <button
                type="button"
                onClick={() => setEscaner(true)}
                className="grid size-12 shrink-0 place-items-center rounded-2xl border borde-base texto-suave transition-colors hover:superficie-hundida hover:texto-base"
                aria-label="Escanear código de barras"
              >
                <ScanLine className="size-5" />
              </button>
            </div>
          </Campo>

          {producto && (
            <Campo etiqueta="Estado">
              <Lista
                value={forma.activo ? 'si' : 'no'}
                onChange={(e) => set('activo', e.target.value === 'si')}
              >
                <option value="si">Activo — se puede vender</option>
                <option value="no">Descontinuado — oculto del listado</option>
              </Lista>
            </Campo>
          )}
        </form>
      </Hoja>

      <EscanerCodigo
        abierto={escaner}
        onCerrar={() => setEscaner(false)}
        onLeer={(codigo) => {
          set('codigoBarras', codigo)
          setEscaner(false)
        }}
      />
    </>
  )
}
